import React from 'react';
import { Card } from '../components/ui/display/Card.jsx';
import { tFor } from '../i18n/i18n.js';
import { formatIsoDate, todayIso, plusDaysIso } from '../lib/price.js';

// Slot picker. Renders a horizontal date strip for the next 14 days plus
// a slot grid for the selected day. The actual schedule fetch happens in
// the BookingWidget orchestrator — this step is pure presentation.

const DAYS = 14;

export function StepSlot({
  days,
  loading,
  selectedDate,
  selectedSlot,
  onSelectDate,
  onSelectSlot,
  locale,
}) {
  const t = tFor(locale);

  // Build the 14-day strip from `days`, but also include any day that
  // has at least one slot from a longer window so we don't visibly drop
  // late-week availability. Cheap: at most 14 cells.
  const strip = buildDateStrip();

  if (loading) {
    return (
      <Card title={t('slot.heading')}>
        <div className="sp-empty">{t('slot.loading')}</div>
      </Card>
    );
  }

  const dayObj = days.find((d) => d.date === selectedDate);
  const slots = dayObj ? dayObj.slots : [];

  return (
    <Card title={t('slot.heading')}>
      <div className="sp-date-strip" role="listbox" aria-label={t('slot.heading')}>
        {strip.map((iso) => {
          const hasSlots =
            days.find((d) => d.date === iso)?.slots?.length > 0;
          const isException =
            days.find((d) => d.date === iso)?.isException;
          const cls = [
            'sp-date-strip__cell',
            iso === selectedDate && 'is-selected',
            !hasSlots && 'is-disabled',
          ].filter(Boolean).join(' ');
          return (
            <button
              key={iso}
              type="button"
              disabled={!hasSlots && !isException}
              onClick={() => hasSlots && onSelectDate(iso)}
              className={cls}
            >
              <div className="sp-date-strip__dow">{dayLabel(iso, locale)}</div>
              <div className="sp-date-strip__num">{dayNum(iso)}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {monthLabel(iso, locale)}
              </div>
            </button>
          );
        })}
      </div>

      {slots.length === 0 ? (
        <div className="sp-empty">{t('slot.empty')}</div>
      ) : (
        <div className="sp-slots">
          {slots.map((slot) => {
            const cls = [
              'sp-slot',
              selectedSlot &&
                selectedSlot.start === slot.start &&
                selectedSlot.end === slot.end &&
                'is-selected',
            ].filter(Boolean).join(' ');
            return (
              <button
                key={`${slot.start}-${slot.end}`}
                type="button"
                className={cls}
                onClick={() => onSelectSlot(slot)}
              >
                {slot.start}
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function buildDateStrip() {
  const out = [];
  const today = todayIso();
  for (let i = 0; i < DAYS; i++) {
    out.push(i === 0 ? today : plusDaysIso(i));
  }
  return out;
}

const DOW_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW_TH = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

function dayLabel(iso, locale) {
  const d = new Date(iso + 'T00:00:00Z');
  const arr = locale === 'th' ? DOW_TH : DOW_EN;
  return arr[d.getUTCDay()];
}

function dayNum(iso) {
  return Number(iso.slice(8, 10));
}

function monthLabel(iso, locale) {
  const d = new Date(iso + 'T00:00:00Z');
  if (locale === 'th') {
    return [
      'ม.ค.',
      'ก.พ.',
      'มี.ค.',
      'เม.ย.',
      'พ.ค.',
      'มิ.ย.',
      'ก.ค.',
      'ส.ค.',
      'ก.ย.',
      'ต.ค.',
      'พ.ย.',
      'ธ.ค.',
    ][d.getUTCMonth()];
  }
  return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getUTCMonth()];
}
